import type { ReactNode } from 'react';

export interface StatusBarProps {
  /** Left-aligned cluster (e.g. save state, word count). */
  left?: ReactNode;
  /** Center cluster — optional; stays collapsed when empty. */
  center?: ReactNode;
  /** Right-aligned cluster (e.g. zoom, language, cursor position). */
  right?: ReactNode;
  /** Landmark label. StatusBar maps to role="status" so consumers
   *  should pass a concise description like "Document status". */
  'aria-label'?: string;
  className?: string;
}

export interface StatusBarItemProps {
  /** Leading icon or indicator. */
  icon?: ReactNode;
  /** Primary text. */
  children?: ReactNode;
  /** Optional tone — `info` / `success` / `warning` / `danger` / `neutral` (default). */
  tone?: 'neutral' | 'info' | 'success' | 'warning' | 'danger';
  /** Click handler — makes the item an interactive button. */
  onClick?: (event: React.MouseEvent) => void;
  /** Accessible label for icon-only items. */
  'aria-label'?: string;
  className?: string;
}

/** Bottom-of-window status line, modeled on the floating-pill shape
 *  described in the visual language spec. Renders as a landmark
 *  `role="status"` + `aria-live="polite"` so screen readers announce
 *  state updates (save, lint result, word count change) without
 *  interrupting the user.
 *
 *  Slots are lazy — passing only `left` produces a left-aligned bar
 *  with no reserved centre/right space; useful on narrow viewports. */
function StatusBarRoot(props: StatusBarProps): ReactNode {
  const { left, center, right, className, 'aria-label': ariaLabel } = props;
  return (
    <div
      role="status"
      aria-label={ariaLabel}
      aria-live="polite"
      className={['vq-statusbar', className].filter(Boolean).join(' ')}
      data-component="StatusBar"
    >
      <div className="vq-statusbar__cluster vq-statusbar__cluster--left">{left}</div>
      {center !== undefined && (
        <div className="vq-statusbar__cluster vq-statusbar__cluster--center">{center}</div>
      )}
      <div className="vq-statusbar__cluster vq-statusbar__cluster--right">{right}</div>
    </div>
  );
}

function StatusBarItem(props: StatusBarItemProps): ReactNode {
  const { icon, children, tone = 'neutral', onClick, className } = props;
  const classes = [
    'vq-statusbar__item',
    `vq-statusbar__item--${tone}`,
    onClick !== undefined && 'vq-statusbar__item--interactive',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const body = (
    <>
      {icon !== undefined && (
        <span className="vq-statusbar__icon" aria-hidden="true">
          {icon}
        </span>
      )}
      {children !== undefined && <span className="vq-statusbar__label">{children}</span>}
    </>
  );

  if (onClick !== undefined) {
    return (
      <button type="button" className={classes} onClick={onClick} aria-label={props['aria-label']}>
        {body}
      </button>
    );
  }
  return (
    <span className={classes} aria-label={props['aria-label']}>
      {body}
    </span>
  );
}

interface StatusBarCompound {
  (props: StatusBarProps): ReactNode;
  Item: typeof StatusBarItem;
}

export const StatusBar: StatusBarCompound = Object.assign(StatusBarRoot, {
  Item: StatusBarItem,
});
